import { useApolloClient } from '@apollo/react-hooks'
import { gql } from 'apollo-boost'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from '@paintscout/ui'
import { contentHash } from '@paintscout/util/builder'
import { useQuote } from '../../../context'
import { getCachedTranslation, setCachedTranslation } from './useTranslatedNoteContent.cache'

// Augment here to guarantee visibility in all webpack/fork-ts-checker
// compilation contexts (symlinked @types packages can be missed by fork-ts-checker v6).
declare module 'paintscout' {
  export interface QuoteDocument {
    translations?: Record<string, Record<string, string>>
  }
}

const TRANSLATE_NOTE_CONTENT = gql`
  query translateNoteContent($notes: [String!]!, $lang: String!) {
    translateNoteContent(notes: $notes, lang: $lang)
  }
`

const EMPTY_TRANSLATIONS: Record<string, string> = {}
const HAS_ALPHA_CHARACTER = /[a-z]/i

export function useTranslatedNoteContent(contents: Array<string | null | undefined>) {
  const apolloClient = useApolloClient()
  const { lang } = useTranslation()
  const { quote } = useQuote()

  const normalizedContents = contents.map((content) => content ?? '')
  const normalizedContentsKey = normalizedContents.join('\u0001')
  const preBakedTranslations = useMemo(
    () => quote?.translations?.[lang] ?? EMPTY_TRANSLATIONS,
    [lang, quote?.translations]
  )
  const preBakedTranslationsKey = normalizedContents
    .map((content) => preBakedTranslations[contentHash(content)] ?? '')
    .join('\u0001')
  const initialTranslatedContents = useMemo(() => {
    seedTranslationCache({ lang, normalizedContents, preBaked: preBakedTranslations })
    return getVisibleContents({
      lang,
      normalizedContents,
      preBaked: preBakedTranslations,
      hideUncached: true
    })
  }, [lang, normalizedContentsKey, preBakedTranslationsKey])
  const [translatedContents, setTranslatedContents] = useState<string[]>(initialTranslatedContents)

  useEffect(() => {
    setTranslatedContents((current) =>
      areContentsEqual(current, initialTranslatedContents) ? current : initialTranslatedContents
    )

    if (!lang || lang === 'en') {
      return
    }

    const uniqueContents = Array.from(
      new Set(normalizedContents.filter((content) => content.trim() && HAS_ALPHA_CHARACTER.test(content)))
    )

    const uncached = uniqueContents.filter((content) => !getCachedTranslation(getCacheKey(lang, content)))
    let cancelled = false

    async function translate() {
      if (uncached.length) {
        try {
          const { data } = await apolloClient.query<{ translateNoteContent: string[] }>({
            query: TRANSLATE_NOTE_CONTENT,
            variables: {
              notes: uncached,
              lang
            },
            fetchPolicy: 'no-cache'
          })

          const translated = data?.translateNoteContent ?? uncached
          uncached.forEach((content, index) => {
            setCachedTranslation(getCacheKey(lang, content), translated[index] ?? content)
          })
        } catch {
          uncached.forEach((content) => {
            setCachedTranslation(getCacheKey(lang, content), content)
          })
        }
      }

      if (cancelled) {
        return
      }

      setTranslatedContents(
        normalizedContents.map((content) => getCachedTranslation(getCacheKey(lang, content)) ?? content)
      )
    }

    void translate()

    return () => {
      cancelled = true
    }
  }, [apolloClient, lang, normalizedContentsKey, preBakedTranslationsKey])
  return translatedContents
}

function getCacheKey(lang: string, content: string) {
  return `${lang}::${content}`
}

function seedTranslationCache({
  lang,
  normalizedContents,
  preBaked
}: {
  lang?: string
  normalizedContents: string[]
  preBaked: Record<string, string>
}) {
  if (!lang || lang === 'en') {
    return
  }

  normalizedContents
    .filter((content) => content.trim() && HAS_ALPHA_CHARACTER.test(content))
    .forEach((content) => {
      const key = getCacheKey(lang, content)
      if (!getCachedTranslation(key)) {
        const preBakedValue = preBaked[contentHash(content)]
        if (preBakedValue) {
          setCachedTranslation(key, preBakedValue)
        }
      }
    })
}

function getVisibleContents({
  lang,
  normalizedContents,
  preBaked,
  hideUncached
}: {
  lang?: string
  normalizedContents: string[]
  preBaked: Record<string, string>
  hideUncached: boolean
}) {
  if (!lang || lang === 'en') {
    return normalizedContents
  }

  return normalizedContents.map((content) => {
    if (!content.trim() || !HAS_ALPHA_CHARACTER.test(content)) {
      return content
    }

    const cached = getCachedTranslation(getCacheKey(lang, content))
    if (cached != null) {
      return cached
    }

    const preBakedValue = preBaked[contentHash(content)]
    if (preBakedValue != null) {
      return preBakedValue
    }

    return hideUncached ? '' : content
  })
}

function areContentsEqual(left: string[], right: string[]) {
  return left.length === right.length && left.every((value, index) => value === right[index])
}
